import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Chip,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import {
  FolderSpecial,
  Difference,
  AccountBalance,
  InsertDriveFile,
} from '@mui/icons-material';

interface DealDoc {
  name: string;
  category: string;
  updatedBy: string;
  date: string;
  size: string;
  status: string;
}

const sampleDocs: DealDoc[] = [
  { name: '100_Bishopsgate_Underwriting_v3.2.xlsx', category: 'Financial Model', updatedBy: 'Senior Associate', date: 'Today, 14:20', size: '4.8 MB', status: 'IC Approved' },
  { name: 'Senior_Debt_Term_Sheet_Barclays.pdf', category: 'Debt Financing', updatedBy: 'VP Real Estate', date: 'Yesterday', size: '1.2 MB', status: 'Agreed' },
  { name: 'Investment_Committee_Deck_Draft.pdf', category: 'IC Materials', updatedBy: 'Director', date: '16 Aug 2026', size: '8.5 MB', status: 'In Review' },
  { name: 'Red_Book_Valuation_Report_CBRE.pdf', category: 'Valuation', updatedBy: 'External Valuation', date: '12 Aug 2026', size: '14.1 MB', status: 'Final' },
];

const FileManagerShowcase: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<number>(0);

  return (
    <Box id="filemanager-section" sx={{ py: 12, bgcolor: '#f8fafc', position: 'relative' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Chip
            icon={<FolderSpecial sx={{ color: '#047857 !important' }} />}
            label="UK REAL ESTATE MODEL & FILE MANAGEMENT"
            sx={{
              bgcolor: 'rgba(4, 120, 87, 0.08)',
              border: '1px solid rgba(4, 120, 87, 0.2)',
              color: '#047857',
              px: 1,
              py: 0.5,
              mb: 2,
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#0f172a',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Intelligent Model & Document Management
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 720,
              mx: 'auto',
              color: '#475569',
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Centralize your UK real estate financial models, debt term sheets, IC presentations, and Stamp Duty (SDLT) calculations in one organized workspace.
          </Typography>
        </Box>

        {/* Card */}
        <Card
          sx={{
            p: { xs: 2.5, md: 4 },
            border: '1px solid #e2e8f0',
            background: '#ffffff',
            boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.06)',
            borderRadius: 3.5,
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#047857', height: 3 },
              }}
            >
              <Tab
                icon={<Difference sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Model Versioning & Diffs"
                sx={{ color: '#64748b', fontWeight: 600, '&.Mui-selected': { color: '#047857' } }}
              />
              <Tab
                icon={<InsertDriveFile sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Deal Document Repository"
                sx={{ color: '#64748b', fontWeight: 600, '&.Mui-selected': { color: '#047857' } }}
              />
              <Tab
                icon={<AccountBalance sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="UK SDLT & Tax Engine"
                sx={{ color: '#64748b', fontWeight: 600, '&.Mui-selected': { color: '#047857' } }}
              />
            </Tabs>
          </Box>

          {/* TAB 1 */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, mb: 1 }}>
                Model Revision Control & Parameter Diffing
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
                Track every iteration made to your UK underwriting cashflows. Compare investment committee drafts side-by-side with zero manual reconciliation.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      borderRadius: 3,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ color: '#047857', fontWeight: 700 }}>
                        Current Active Model (v3.2 IC Final)
                      </Typography>
                      <Chip label="LIVE MODEL" size="small" sx={{ bgcolor: '#047857', color: '#ffffff', fontWeight: 800 }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 2 }}>
                      Updated today by Senior Underwriter
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', border: '1px solid #a7f3d0', borderRadius: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#0f172a' }}>Exit Cap Rate:</Typography>
                        <Typography variant="body2" sx={{ color: '#047857', fontWeight: 700, fontFamily: 'monospace' }}>4.75% (Yield Compressed -25bps)</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', border: '1px solid #a7f3d0', borderRadius: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#0f172a' }}>Senior Debt Margin:</Typography>
                        <Typography variant="body2" sx={{ color: '#047857', fontWeight: 700, fontFamily: 'monospace' }}>SONIA + 2.25% (Refinanced)</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', border: '1px solid #a7f3d0', borderRadius: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#0f172a' }}>Net Exit GDV:</Typography>
                        <Typography variant="body2" sx={{ color: '#047857', fontWeight: 800, fontFamily: 'monospace' }}>£52.4M (+£2.4M Delta)</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 3,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ color: '#0f172a', fontWeight: 700 }}>
                        Previous Version (v3.1 Draft)
                      </Typography>
                      <Chip label="ARCHIVED" size="small" sx={{ bgcolor: '#e2e8f0', color: '#64748b' }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                      Updated 2 days ago by Associate
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>Exit Cap Rate:</Typography>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontFamily: 'monospace' }}>5.00%</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>Senior Debt Margin:</Typography>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontFamily: 'monospace' }}>SONIA + 2.65%</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>Net Exit GDV:</Typography>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontFamily: 'monospace' }}>£50.0M</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* TAB 2 */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
                    Centralized Underwriting & Deal File Storage
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    Keep deal models, lender term sheets, valuation reports, and IC materials organized per asset.
                  </Typography>
                </Box>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 3 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ color: '#0f172a', fontWeight: 700 }}>Document Name</TableCell>
                      <TableCell sx={{ color: '#0f172a', fontWeight: 700 }}>Category</TableCell>
                      <TableCell sx={{ color: '#0f172a', fontWeight: 700 }}>Uploaded By</TableCell>
                      <TableCell sx={{ color: '#0f172a', fontWeight: 700 }}>Last Modified</TableCell>
                      <TableCell align="right" sx={{ color: '#0f172a', fontWeight: 700 }}>Size</TableCell>
                      <TableCell align="center" sx={{ color: '#0f172a', fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sampleDocs.map((doc, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f1f5f9' } }}>
                        <TableCell sx={{ color: '#0f172a', fontWeight: 700 }}>{doc.name}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>{doc.category}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>{doc.updatedBy}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>{doc.date}</TableCell>
                        <TableCell align="right" sx={{ color: '#0f172a', fontFamily: 'monospace' }}>{doc.size}</TableCell>
                        <TableCell align="center"><Chip label={doc.status} size="small" sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700, fontSize: '0.7rem' }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* TAB 3 */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, mb: 1 }}>
                Automated UK Stamp Duty Land Tax (SDLT) Engine
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
                Instant commercial property SDLT calculation based on UK HMRC tax brackets for freehold acquisitions and lease NPV calculations.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 3, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 3 }}>
                    <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 700 }}>ACQUISITION PRICE</Typography>
                    <Typography variant="h3" sx={{ color: '#0f172a', fontWeight: 800, fontFamily: 'monospace', my: 1 }}>£45,000,000</Typography>
                    <Typography variant="caption" sx={{ color: '#475569' }}>UK Commercial Property Transaction</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 3, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 3 }}>
                    <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700 }}>TOTAL SDLT LIABILITY</Typography>
                    <Typography variant="h3" sx={{ color: '#047857', fontWeight: 800, fontFamily: 'monospace', my: 1 }}>£2,234,500</Typography>
                    <Typography variant="caption" sx={{ color: '#475569' }}>Effective Tax Rate: 4.96%</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 3 }}>
                    <Typography variant="caption" sx={{ color: '#6d28d9', fontWeight: 700 }}>TAX SLAB BREAKDOWN</Typography>
                    <Typography variant="body2" sx={{ color: '#0f172a', mt: 1, fontFamily: 'monospace' }}>£0 - £150k @ 0% = £0</Typography>
                    <Typography variant="body2" sx={{ color: '#0f172a', fontFamily: 'monospace' }}>£150k - £250k @ 2% = £2,000</Typography>
                    <Typography variant="body2" sx={{ color: '#0f172a', fontFamily: 'monospace' }}>&gt;£250k @ 5% = £2,232,500</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Card>
      </Container>
    </Box>
  );
};

export default FileManagerShowcase;
